import React, { useCallback } from 'react';
import classnames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuItemGroup } from 'ui-components/components/dropdown-menu-item-group';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { Text, TextType } from 'ui-components/components/text';
import { noop } from 'ui-components/default-props/noop';

import { EmptyCarrierServicesWarning } from './empty-carrier-services-warning';
import messages from './messages';
import style from './style.module.less';
import { emptyArray } from 'ui-components/default-props/empty';

const CLEAR_ALL = 'clear-all';
const APPLY_SELECTIONS = 'apply-selections';

/** @typedef {import('../../prop-types/shipping-strategy-carrier-service').ShippingStrategyCarrierService} ShippingStrategyCarrierService */
/** @typedef {import('react-intl').MessageDescriptor} MessageDescriptor */

/**
 * @typedef {object} GroupedCarrierService
 * @property {string} groupId
 * @property {MessageDescriptor} name
 * @property {string | null} accountNickname
 * @property {ShippingStrategyCarrierService[]} items
 * */

/**
 *
 * @param {object} props
 * @param {string} props.className
 * @param {boolean} props.disabled
 * @param {GroupedCarrierService[]} [props.groupedCarrierServices] `emptyArray` by default
 * @param {MessageDescriptor} props.instructionalText
 * @param {MessageDescriptor} props.label
 * @param {(arg: ShippingStrategyCarrierService[]) => void} props.onServicesConfirmed
 * @param {() => void} props.onServicesCleared
 * @param {ShippingStrategyCarrierService[]} [props.unsavedStrategyServices] `emptyArray` by default
 * @param {(arg: ShippingStrategyCarrierService[]) => void} props.onSetUnsavedStrategyServices
 * @returns {React.JSX.Element}
 */
export const CarrierServiceSelector = ({
  className,
  disabled,
  groupedCarrierServices = emptyArray,
  instructionalText,
  label: parentProvidedLabel,
  onServicesConfirmed,
  onServicesCleared,
  unsavedStrategyServices = emptyArray,
  onSetUnsavedStrategyServices,
}) => {
  const intl = useIntl();
  const hasNoConfiguredOrVisibleServices = !groupedCarrierServices.length;
  const hasServiceAdded = useCallback(
    (serviceId, sellerLabelProviderId) => {
      if (!unsavedStrategyServices || !unsavedStrategyServices.length) {
        return false;
      }
      return unsavedStrategyServices.some(
        (service) => service.serviceId === serviceId && service.sellerLabelProviderId === sellerLabelProviderId,
      );
    },
    [unsavedStrategyServices],
  );

  const onItemChecked = useCallback(
    (item) => {
      const { serviceId, sellerLabelProviderId } = item;
      const serviceAlreadyAdded = hasServiceAdded(serviceId, sellerLabelProviderId);
      if (serviceAlreadyAdded) {
        onSetUnsavedStrategyServices(
          unsavedStrategyServices.filter(
            (service) => !(service.serviceId === serviceId && service.sellerLabelProviderId === sellerLabelProviderId),
          ),
        );
        return;
      }
      onSetUnsavedStrategyServices([...unsavedStrategyServices, item]);
    },
    [hasServiceAdded, unsavedStrategyServices, onSetUnsavedStrategyServices],
  );

  const shouldAllowDropdownToClose = useCallback(({ target: { id } }) => {
    return [CLEAR_ALL, APPLY_SELECTIONS].includes(id);
  }, []);

  const onConfirmServices = useCallback(() => {
    onServicesConfirmed(unsavedStrategyServices);
  }, [onServicesConfirmed, unsavedStrategyServices]);

  return (
    <div className={classnames(style['carrier-service-selector'], className)}>
      {parentProvidedLabel ? <Text textType={TextType.Label} value={parentProvidedLabel} /> : null}
      <DropdownMenu
        closeWhenChildrenClicked={false}
        containerAllowedMeToClose={shouldAllowDropdownToClose}
        disabled={disabled}
        dropdownMenuShouldHaveWidthOfButton
        label=''
        menuPosition='bottom-right'
      >
        <DropdownMenuToggler className={style['menu-toggler']} theme='tertiary'>
          <FormattedMessage {...messages.selectServiceText} />
        </DropdownMenuToggler>
        <div className={style['menu-work-area']}>
          {hasNoConfiguredOrVisibleServices ? <EmptyCarrierServicesWarning /> : null}
          {groupedCarrierServices.map((group) => {
            const accountNicknameOrEmpty = group.accountNickname?.length ? `(${group.accountNickname})` : '';
            const groupName = `${intl.formatMessage(group.name, group.name?.values)} ${accountNicknameOrEmpty}`;
            return (
              <DropdownMenuItemGroup key={group.groupId} label={groupName}>
                {group.items.map((item) => (
                  <DropdownMenuItem
                    key={`${item.serviceId}-${item.sellerLabelProviderId}`}
                    className={style['menu-item']}
                    onClick={() =>
                      onItemChecked({
                        ...item,
                        accountNickname: group.accountNickname,
                      })
                    }
                    value={item.serviceId}
                  >
                    <Checkbox checked={hasServiceAdded(item.serviceId, item.sellerLabelProviderId)} onChange={noop} />
                    {item.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuItemGroup>
            );
          })}
          {!hasNoConfiguredOrVisibleServices && (
            <div className={style['menu-footer']}>
              <Button disabled={disabled} id={CLEAR_ALL} onClick={onServicesCleared} theme='link'>
                <FormattedMessage {...messages.clearSelectionsButtonText} />
              </Button>
              <Button disabled={disabled} id={APPLY_SELECTIONS} onClick={onConfirmServices} theme='primary'>
                <FormattedMessage {...messages.applySelectionsButtonText} />
              </Button>
            </div>
          )}
        </div>
      </DropdownMenu>
      {instructionalText ? (
        <div className={style['instructional-text']}>
          <FormattedMessage {...instructionalText} />
        </div>
      ) : null}
    </div>
  );
};
